import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { HTVButtonControllerDirective } from '../../../app-button-controller.directive'
import { Subscription } from 'rxjs'
import { ChannelTrackData, ChannelTrackDataFlags } from '../../../interfaces/channel-track-data'
import { LogService } from 'htv-lib'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { VODVideo } from '../../../interfaces/vod'
import { LanguageService } from '../../../services/language.service'

interface ExtendedChannelTrackData {
  data: ChannelTrackData
  nativeLanguage: string
}

@Component({
  selector: 'app-vod-tracks-menu',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatIconModule, MatButtonModule],
  templateUrl: './vod-tracks-menu.component.html',
  styleUrls: ['./vod-tracks-menu.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class VODTracksMenuComponent implements OnInit, OnDestroy, OnChanges {
  tracksSubscription?: Subscription

  @Input() vod!: VODVideo
  @Input() visible = false
  @Output() hide = new EventEmitter()
  @Output() audioTrack = new EventEmitter<ChannelTrackData>()
  @Output() subtitleTrack = new EventEmitter<{ track: ChannelTrackData; enabled: boolean }>()

  isSubtitleEnabled = false
  currentAudioTrack?: ExtendedChannelTrackData
  currentSubtitleTrack?: ExtendedChannelTrackData
  audioTracks: ChannelTrackData[] = []
  subtitleTracks: ChannelTrackData[] = []
  extendedAudioTracks: ExtendedChannelTrackData[] = []
  extendedSubtitleTracks: ExtendedChannelTrackData[] = []

  readonly ChannelTrackDataFlags = ChannelTrackDataFlags

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private languageService: LanguageService,
    private logService: LogService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.reset()
    this.registerTracksListener()
  }

  ngOnDestroy(): void {
    this.tracksSubscription?.unsubscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.appButtonDirective.forceRefreshSelectedButton()
    }
  }

  reset(): void {
    this.tracksSubscription?.unsubscribe()
    this.isSubtitleEnabled = false
    this.currentAudioTrack = undefined
    this.currentSubtitleTrack = undefined
    this.audioTracks = []
    this.subtitleTracks = []
    this.extendedAudioTracks = []
    this.extendedSubtitleTracks = []
  }

  registerTracksListener(): void {
    this.tracksSubscription = this.vod.state?.tracks$.subscribe(channelData => {
      try {
        if (channelData) {
          let hasListChanges = false

          if (this.isSubtitleEnabled != channelData.isSubtitleEnabled) {
            this.isSubtitleEnabled = channelData.isSubtitleEnabled
            this.vod.state?.subtitleEnabled$.next(this.isSubtitleEnabled)
          }

          if (JSON.stringify(this.currentAudioTrack?.data) != JSON.stringify(channelData.currentAudioTrack)) {
            if (channelData.currentAudioTrack) {
              this.currentAudioTrack = this.mapToExtendedChannelTrackData(channelData.currentAudioTrack)
            } else {
              this.currentAudioTrack = undefined
            }
          }

          if (JSON.stringify(this.currentSubtitleTrack) != JSON.stringify(channelData.currentSubtitleTrack)) {
            if (channelData.currentSubtitleTrack) {
              this.currentSubtitleTrack = this.mapToExtendedChannelTrackData(channelData.currentSubtitleTrack)
            } else {
              this.currentSubtitleTrack = undefined
            }
          }

          if (JSON.stringify(this.audioTracks) != JSON.stringify(channelData.audioTracks)) {
            hasListChanges = true
            this.audioTracks = channelData.audioTracks
            this.extendedAudioTracks = channelData.audioTracks.map(at => this.mapToExtendedChannelTrackData(at))
          }

          if (JSON.stringify(this.subtitleTracks) != JSON.stringify(channelData.subtitleTracks)) {
            hasListChanges = true
            this.subtitleTracks = channelData.subtitleTracks
            this.extendedSubtitleTracks = channelData.subtitleTracks.map(st => this.mapToExtendedChannelTrackData(st))
          }

          // Reload button directive because lists have changed
          if (hasListChanges && this.visible) {
            this.appButtonDirective.initSelectedButton()
          }
        } else {
          // do nothing
        }
      } catch (err: any) {
        this.logService.debug(`[VODTracksMenuComponent] Could not retrieve VOD tracks, error = ${err?.message}`)
      }
    })
  }

  setAudioTrack(track: ChannelTrackData): void {
    this.audioTrack.emit(track)
  }

  async setSubtitleTrack(track: ChannelTrackData): Promise<void> {
    if (this.currentSubtitleTrack?.data.index == track.index) {
      if (this.isSubtitleEnabled) {
        this.subtitleTrack.emit({ track, enabled: false })
      } else {
        this.subtitleTrack.emit({ track, enabled: true })
      }
    } else {
      this.subtitleTrack.emit({ track, enabled: true })
    }
  }

  private mapToExtendedChannelTrackData(data: ChannelTrackData): ExtendedChannelTrackData {
    const nativeLanguage = this.languageService.getNativeLanguage(data.lang)
    return {
      data: data,
      nativeLanguage: nativeLanguage,
    }
  }

  close(): void {
    this.hide.emit()
  }
}
